import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { Activity, DollarSign, TrendingDown, Clock, MapPin, Search, FileText, RefreshCw, Bell } from 'lucide-react';
import { Button } from '../../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ApiUsage = () => {
  const { token } = useAuth();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchUsage = async () => {
    try {
      const response = await axios.get(`${API}/api-usage`, { headers });
      setUsage(response.data);
    } catch (error) {
      console.error('Failed to fetch API usage:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchUsage();
  }, [token]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchUsage();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  if (!usage) {
    return (
      <div className="text-center text-muted-foreground py-12">
        Failed to load API usage data
      </div>
    );
  }

  const freeCredit = 200;
  const usedCredit = usage.this_month.estimated_cost_usd;
  const creditPercentage = Math.min((usedCredit / freeCredit) * 100, 100);
  const remainingCredit = usage.this_month.free_credit_remaining_usd;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold" data-testid="api-usage-title">Google Places API Usage</h2>
          <p className="text-muted-foreground">Monitor your API calls and costs</p>
        </div>
        <Button 
          variant="outline" 
          onClick={handleRefresh} 
          disabled={refreshing}
          data-testid="refresh-usage-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Monthly Credit Card */}
      <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <DollarSign className="w-5 h-5 text-primary" />
            Monthly Free Credit
          </CardTitle>
          <CardDescription>$200/month free tier from Google</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span>Used: ${usedCredit.toFixed(2)}</span>
              <span>Remaining: ${remainingCredit.toFixed(2)}</span>
            </div>
            <Progress value={creditPercentage} className="h-3" data-testid="credit-progress" />
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground">
                {creditPercentage.toFixed(1)}% of free tier used
              </span>
              {creditPercentage > 80 ? (
                <Badge variant="destructive" data-testid="credit-warning">
                  Low Credit
                </Badge>
              ) : creditPercentage > 50 ? (
                <Badge variant="outline" className="border-yellow-500 text-yellow-500">
                  Moderate Usage
                </Badge>
              ) : (
                <Badge variant="outline" className="border-green-500 text-green-500" data-testid="credit-healthy">
                  Healthy
                </Badge>
              )}
            </div>
            {/* Alert Status */}
            {usage.alerts && (
              <div className="pt-2 border-t mt-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Bell className="w-3 h-3" />
                    Email alert at {usage.alerts.threshold_percentage}% (${usage.alerts.alert_will_trigger_at_usd})
                  </span>
                  {usage.alerts.alert_sent_this_month ? (
                    <Badge variant="outline" className="text-xs">Alert Sent</Badge>
                  ) : creditPercentage >= usage.alerts.threshold_percentage ? (
                    <Badge variant="destructive" className="text-xs">Alert Pending</Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs border-green-500 text-green-500">Not Triggered</Badge>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Today's Calls */}
        <Card data-testid="today-stats-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              Today
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{usage.today.total_calls}</div>
            <p className="text-xs text-muted-foreground">API calls</p>
          </CardContent>
        </Card>

        {/* This Month's Calls */}
        <Card data-testid="month-stats-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Activity className="w-4 h-4 text-muted-foreground" />
              This Month
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{usage.this_month.total_calls}</div>
            <p className="text-xs text-muted-foreground">API calls (${usage.this_month.estimated_cost_usd})</p>
          </CardContent>
        </Card>

        {/* Total Calls */}
        <Card data-testid="total-stats-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Activity className="w-4 h-4 text-muted-foreground" />
              All Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{usage.total.total_calls}</div>
            <p className="text-xs text-muted-foreground">Total API calls</p>
          </CardContent>
        </Card>

        {/* Efficiency */}
        <Card data-testid="efficiency-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-muted-foreground" />
              Efficiency
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{usage.efficiency.details_per_search}</div>
            <p className="text-xs text-muted-foreground">Details calls per search</p>
          </CardContent>
        </Card>
      </div>

      {/* Breakdown by Call Type */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card data-testid="geocode-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <MapPin className="w-4 h-4 text-blue-500" />
              Geocode
            </CardTitle>
            <CardDescription>Postcode to coordinates</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Today</span>
                <span className="font-medium">{usage.today.geocode}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">This Month</span>
                <span className="font-medium">{usage.this_month.geocode}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Total</span>
                <span className="font-medium">{usage.total.geocode}</span>
              </div>
              <div className="pt-2 border-t">
                <span className="text-xs text-muted-foreground">Cost: {usage.pricing_info.geocode_per_1000}/1000</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="search-nearby-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Search className="w-4 h-4 text-green-500" />
              Nearby Search
            </CardTitle>
            <CardDescription>Find businesses in area</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Today</span>
                <span className="font-medium">{usage.today.search_nearby}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">This Month</span>
                <span className="font-medium">{usage.this_month.search_nearby}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Total</span>
                <span className="font-medium">{usage.total.search_nearby}</span>
              </div>
              <div className="pt-2 border-t">
                <span className="text-xs text-muted-foreground">Cost: {usage.pricing_info.search_nearby_per_1000}/1000</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="place-details-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <FileText className="w-4 h-4 text-purple-500" />
              Place Details
            </CardTitle>
            <CardDescription>Get reviews & photos</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Today</span>
                <span className="font-medium">{usage.today.place_details}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">This Month</span>
                <span className="font-medium">{usage.this_month.place_details}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Total</span>
                <span className="font-medium">{usage.total.place_details}</span>
              </div>
              <div className="pt-2 border-t">
                <span className="text-xs text-muted-foreground">Cost: {usage.pricing_info.place_details_per_1000}/1000</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent API Calls */}
      <Card data-testid="recent-calls-card">
        <CardHeader>
          <CardTitle className="text-lg">Recent API Calls</CardTitle>
          <CardDescription>Last 20 API requests</CardDescription>
        </CardHeader>
        <CardContent>
          {usage.recent_calls.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">No API calls recorded yet</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {usage.recent_calls.map((call, index) => (
                <div 
                  key={index} 
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                  data-testid={`recent-call-${index}`}
                >
                  <div className="flex items-center gap-3">
                    {call.call_type === 'geocode' && <MapPin className="w-4 h-4 text-blue-500" />}
                    {call.call_type === 'search_nearby' && <Search className="w-4 h-4 text-green-500" />}
                    {call.call_type === 'place_details' && <FileText className="w-4 h-4 text-purple-500" />}
                    <div>
                      <p className="text-sm font-medium capitalize">{call.call_type.replace('_', ' ')}</p>
                      <p className="text-xs text-muted-foreground">
                        {call.details?.postcode && `Postcode: ${call.details.postcode}`}
                        {call.details?.place_id && `Place ID: ${call.details.place_id.substring(0, 20)}...`}
                        {call.details?.types && ` | Types: ${call.details.types.join(', ')}`}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">
                      {new Date(call.timestamp).toLocaleString()}
                    </p>
                    {call.details?.results_count !== undefined && (
                      <Badge variant="outline" className="text-xs">
                        {call.details.results_count} results
                      </Badge>
                    )}
                    {call.details?.reviews_count !== undefined && (
                      <Badge variant="outline" className="text-xs">
                        {call.details.reviews_count} reviews
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pricing Info */}
      <Card className="bg-muted/30">
        <CardHeader>
          <CardTitle className="text-lg">Pricing Reference</CardTitle>
          <CardDescription>Google Places API (New) pricing</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <h4 className="font-medium">API Costs</h4>
              <ul className="space-y-1 text-muted-foreground">
                <li>• Geocode: $5.00 per 1,000 requests</li>
                <li>• Nearby Search: $6.50 per 1,000 requests</li>
                <li>• Place Details: $5.00 per 1,000 requests</li>
              </ul>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium">Free Tier</h4>
              <ul className="space-y-1 text-muted-foreground">
                <li>• $200 monthly credit from Google</li>
                <li>• Roughly ~1,000 postcode searches</li>
                <li>• FieldMasks keep us in cheaper tier</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ApiUsage;
